import { AppEnv } from '@brightdrop/app-config';
import type { VehicleWithDriverAndPeripheralsResponse } from '@gm-commercial/rtk-clients/assets/types';
import { defaultsDeep } from 'lodash-es';
import type { RootState } from '~/app/rootReducer';
import {
  API_VERSION_DEFAULTS,
  ASSETS_API_BASE_CONFIG,
  BD_FLEET_HEADER_NAME,
  CONTENT_TYPE_HEADER,
  CONTENT_TYPE_VALUE,
  CS_CONTINUATION_TOKEN_HEADER_NAME,
  DEFAULT_API_CONFIG,
  REMOTE_COMMAND_API_CONFIG,
} from '~/common/apis/api.constants';
import {
  ACCEPTED_EXPORT_FILE_TYPE,
  SearchConfigType,
  SessionConfigType,
} from '~/common/constants/common.constant';
import type { ApiResponse } from '~/common/models/apis/apiResponse.model';
import {
  type AddAssetFormFields,
  type ApiAsset,
  type ApiAssetRecall,
  type ApiAssetTelemetryKey,
  type ApproveVehicle,
  type Asset,
  type AssetComplianceDetail,
  type AssetEvent,
  AssetEventAction,
  type AssetIgnitionStatus,
  type AssetModel,
  type AssetProtectedFormFields,
  type AssetRecall,
  type AssetRemoteCommandExecuteResult,
  type AssetServiceStatus,
  type AssetStatusResult,
  type RejectApproveVehiclesResult,
  type RejectVehicle,
  type RetryVehicleRequestBody,
  type RetryVehicleResponseBody,
} from '~/common/models/asset.model';
import {
  type AdvisorVehicleListItem,
  ASSET_REPORT_FIELD_NAMES,
  type AssetReportItem,
  type PendingVehicleItem,
} from '~/common/models/asset-report.model';
import type {
  AssetRawLocation,
  FormPayload,
  PagedResult,
  PagedResultWithErrors,
} from '~/common/models/common.model';
import { BDAppErrorType, BDError } from '~/common/models/error.model';
import {
  BATTERY_12V_PROGNOSTICS_FLAG,
  CHARGING_SCHEDULE_FLAG_NAME,
  MARCH_VEHICLE_MANAGEMENT_FLAG,
  NEW_CONNECTIVITY_STATUS_FLAG,
  NOT_EQUIPPED_FLAG,
} from '~/common/models/featureFlags.model';
import type { ApiRecallsItem } from '~/common/models/recalls.model';
import type { VehicleAssignmentItem } from '~/common/models/vehicle.model';
import {
  addAcceptLanguageHeader,
  addCSFleetIdHeader,
  addCSOrganizationIdHeader,
  addHeader,
  addOrganizationIdHeader,
  hasApiResult,
  resultIsArray,
} from '~/common/utils/apis/api.utils';
import {
  makeDeletePayloadCreator,
  makeGetPayloadCreator,
  makePostPayloadCreator,
  makePutPayloadCreator,
  makeThunk,
  makeThunkCreatorConfig,
} from '~/common/utils/store/thunk.helper';
import { PeripheralVehiclesConfig } from '~/routes/organizations/devices/peripheral/config/constants/state';
import {
  type AssetHistoryParams,
  type AssetListSession,
  type AssetPaginatedListParams,
  type AssetParams,
  AssetReportConfigType,
  type AssetRouteParams,
  type PendingVehiclePaginatedListParams,
  type PendingVehicleResponse,
  type SearchFilterInfo,
  type UpdateAssetFormPayload,
} from '../assetsSlice';
import type { AssetListParams } from '../form/utils/assetForm.utils';
import { mapToVehicleAssignmentItems } from '../list/assign/utils/vehicleAssignmentList.utils';
import {
  checkDiagnosticsError,
  mapApiAssetComplianceToAssetComplianceDetail,
  mapAssetApiResponseToAsset,
  mapAssetFormToApiRequest,
  mapEventApiResponseToEvent,
  mapGeoJSONApiResponseToLocation,
  mapVehicleDetailsRecalls,
} from '../mappers/asset.mappers';
import {
  type ApiBatteryReadinessItem,
  type ApiReadinessItem,
  type ApiTiresReadinessItem,
  mapApiAdvisorVehicleListItem,
  mapApiAssetBatteryReadinessToReportItem,
  mapApiAssetDiagnosticsToReportItem,
  mapApiAssetReadinessToReportItem,
  mapApiAssetTireReadinessToReportItem,
  mapApiRecallsToReportItem,
  mapLegacyApiAssetReadinessToReportItem,
} from '../report/mappers/asset-report.mappers';
import {
  buildAdvisorVehicleQueryParam,
  buildAssetComplianceQuery,
  buildAssetQuery,
  buildAssetSort,
  buildVehicleQuery,
  formatFilterCriteriaForVehicles,
  formatSearchCriteriaForVehicles,
} from '../utils/assets.utils';

const getConnectivityFeatureFlags = (state: RootState) => ({
  isNotEquippedFeatureFlag: Boolean(
    state.featureFlags.entities[NOT_EQUIPPED_FLAG]?.value
  ),
  isNewConnectivityStatusFeatureFlag: Boolean(
    state.featureFlags.entities[NEW_CONNECTIVITY_STATUS_FLAG]?.value
  ),
});

const BASE_CONFIG = ASSETS_API_BASE_CONFIG;
const VEHICLE_LIST_CONFIG = {
  ...DEFAULT_API_CONFIG,
  axiosConfig: {
    ...DEFAULT_API_CONFIG.axiosConfig,
    timeout:
      globalThis.appConfig.env === AppEnv.DEV ||
      globalThis.appConfig.env === AppEnv.QA
        ? 15000
        : 45000, //TODO:Update timeout for production after validating the performance
  },
};
const VEHICLE_LIST_API_EXPORT_CONFIG = makeThunkCreatorConfig({
  timeout:
    globalThis.appConfig.env === AppEnv.DEV ||
    globalThis.appConfig.env === AppEnv.QA
      ? 15000
      : 45000, //TODO:Update timeout for production after validating the performance
  retries: 2,
  delay: 100,
  customHeaders: {
    [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE,
    Accept: ACCEPTED_EXPORT_FILE_TYPE,
  },
});

enum AssetsActionType {
  GET_ASSETS = 'assets/fetchAssets',
  GET_VEHICLE_NOTIFICATION_ASSETS = 'assets/getVehicleNotificationAssets',
  GET_PENDING_VEHICLES = 'boarding/vehicles/pending',
  GET_PERIPHERAL_VEHICLES = 'assets/getPeripheralVehicles',
  GET_READINESS_REPORT = 'assets/getReadinessReport',
  GET_VEHICLES_WITH_DRIVERS_AND_PERIPHERALS = 'assets/getVehiclesWithDriversAndPeripherals',
  GET_TIRES_REPORT = 'assets/getTiresReport',
  GET_BATTERY_REPORT = 'assets/getBatteryReport',
  GET_VEHICLE_DIAGNOSTICS_LIST = 'assets/getVehicleDiagnosticsList',
  GET_RECALLS = 'assets/getRecalls',
  GET_ASSETS_SEARCH = 'assets/fetchAssetsSearch',
  GET_ASSETS_COMPLIANCE = 'assets/getAssetsCompliance',
  GET_ASSET_DETAILS = 'assets/fetchDetails',
  GET_ASSET_RECALLS = 'assets/fetchRecalls',
  GET_ASSET_HISTORY = 'assets/fetchHistory',
  GET_ASSET_LOCATION = 'asset/fetchLocation',
  ADD_ASSET = 'assets/addNewAsset',
  UPDATE_ASSET = 'assets/updateAsset',
  LOCK_ASSET = 'assets/lockAsset',
  UPDATE_AVAILABILITY = 'assets/updateServiceAvailability',
  REPROVISION_ASSET = 'assets/reProvisionAsset',
  REMOTE_COMMAND_ASSET = 'assets/executeRemoteCommand',
  GET_ASSET_STATUS = 'assets/getAssetStatus',
  GET_ASSET_IGNITION = 'assets/getAssetIgnition',
  REJECT_VEHICLE = 'REJECT_VEHICLE',
  RETRY_VEHICLE = 'RETRY_VEHICLE',
  APPROVE_VEHICLE = 'APPROVE_VEHICLE',
  SET_ACTIVE_BATCH_ID = 'assets/setActiveBatchId',
  /* TODO: following actions should be reviewed for removal */
  DELETE_ASSET = 'assets/deleteAsset',
  GET_NEXT_EP_SERIAL = 'assets/getNextEPalletSerial',
  GET_TRACKER_SERIALS = 'assets/getTrackerSerials',
  GET_EP_VERSIONS = 'assets/getEPalletVersions',
  GET_VEHICLE_LIST_FLUIDS_EXPORT = 'assets/getFluidsExport',
  GET_ADVISOR_VEHICLE_LIST = 'assets/getAdvisorVehicleList',
}

const getAssets = makeThunk(
  AssetsActionType.GET_ASSETS,
  makeGetPayloadCreator<ApiResponse<Asset[]>, AssetListParams>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: (routeParams) => {
      return {
        requestParams: {
          organizationId: routeParams.organizationsId || '',
          fleetId: routeParams.fleetsId || '',
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>, state) => {
      if (response && resultIsArray<Partial<ApiAsset>>(response)) {
        const errors: BDError[] = [];
        const { isNotEquippedFeatureFlag, isNewConnectivityStatusFeatureFlag } =
          getConnectivityFeatureFlags(state);
        const result = {
          ...response,
          result: response.result?.length
            ? response.result.reduce((mappedAssets, apiAsset) => {
                try {
                  mappedAssets.push(
                    mapAssetApiResponseToAsset(
                      apiAsset,
                      isNotEquippedFeatureFlag,
                      isNewConnectivityStatusFeatureFlag,
                      {
                        isTwelveVoltBatteryEnabled: Boolean(
                          state.featureFlags.entities[
                            BATTERY_12V_PROGNOSTICS_FLAG
                          ]?.value
                        ),
                      }
                    )
                  );
                } catch (e) {
                  // handle mapping errors without bombing entire list response
                  const error = {
                    name: 'Get Assets Mapping Error',
                    type: BDAppErrorType.VALIDATION,
                    message:
                      e instanceof Error
                        ? e.message
                        : 'Failed to map asset API response',
                    data: apiAsset,
                  };
                  errors.push(BDError.asJson(error));
                }
                return mappedAssets;
              }, [] as Asset[])
            : [],
        };
        if (errors.length) {
          result.errors = errors;
        }

        return result;
      }
      // bomb entire list response if response does not match expected format
      throw new BDError('Unexpected asset list response', { data: response });
    },
  })
);

const getPendingVehicles = makeThunk(
  AssetsActionType.GET_PENDING_VEHICLES,
  makeGetPayloadCreator<
    ApiResponse<PendingVehicleResponse>,
    PendingVehiclePaginatedListParams
  >({
    url: (params) =>
      `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.pendingVehiclesListAPI}/boarding/vehicles/pending?`
        .concat(params.rowsPerPage ? `size=${params.rowsPerPage}` : '')
        .concat(params.searchTerm ? `&vin=${params.searchTerm}` : ''),
    axiosOptions: ({ organizationsId, continuationToken }, state) =>
      addAcceptLanguageHeader(
        addCSOrganizationIdHeader(
          continuationToken
            ? addHeader(
                BASE_CONFIG,
                CS_CONTINUATION_TOKEN_HEADER_NAME,
                continuationToken
              )
            : BASE_CONFIG,
          organizationsId
        ),
        state.profile.currentLocale
      ),
    responseAdapter: (
      response: unknown
    ): ApiResponse<PendingVehicleResponse> => {
      if (response && typeof response === 'object' && 'result' in response) {
        const apiResponse = response as ApiResponse<PendingVehicleResponse>;
        return {
          ...apiResponse,
          result: {
            totalItems: apiResponse.result.totalItems,
            token: apiResponse.result.token,
            items: Array.isArray(apiResponse.result?.items)
              ? (apiResponse.result.items.map((item) =>
                  defaultsDeep({}, item, {
                    sessionId: '',
                    vin: '',
                    organizationId: '',
                    createdOn: '',
                    status: '',
                    vehicleYear: '',
                    vehicleMake: '',
                    vehicleModel: '',
                    vehicleImageSrc: '',
                  })
                ) as PendingVehicleItem[])
              : [],
          },
        };
      }
      throw new BDError('Unexpected pending vehicles response format', {
        data: response,
      });
    },
  })
);

const getVehicleListFluidsExport = makeThunk(
  AssetsActionType.GET_VEHICLE_LIST_FLUIDS_EXPORT,
  makePostPayloadCreator<BlobPart, AssetParams>({
    url: (_params) => {
      return `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.vehicleListEnhancementPhase1API}/views/fluidReadinessReport/export`;
    },
    axiosOptions: ({ organizationsId, fleetsId }, state) =>
      addAcceptLanguageHeader(
        addCSFleetIdHeader(
          addCSOrganizationIdHeader(
            VEHICLE_LIST_API_EXPORT_CONFIG,
            organizationsId
          ),
          fleetsId
        ),
        state.profile.currentLocale
      ),
    argAdapter: ({ sessionId }, state) => {
      const sessionConfig = getAssetListQuerySessionConfig({
        sessionId,
        state,
      });
      const query = buildVehicleQuery(sessionConfig, ASSET_REPORT_FIELD_NAMES);
      const sort = buildAssetSort(sessionConfig, ASSET_REPORT_FIELD_NAMES);
      return {
        requestBody: {
          query,
          sort,
        },
      };
    },
  })
);

const getVehicleNotificationAssets = makeThunk(
  AssetsActionType.GET_VEHICLE_NOTIFICATION_ASSETS,
  makePostPayloadCreator<
    ApiResponse<PagedResultWithErrors<AssetReportItem>>,
    AssetPaginatedListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/views/readinessReport`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: (
      { page, rowsPerPage: size, sessionId, skipStateUpdate, ...params },
      state
    ) => {
      const sessionConfig = getAssetListQuerySessionConfig({
        sessionId,
        state,
      });

      // Include selection criteria only for dialog data fetching (skipStateUpdate is true)
      // Skip selection criteria for normal pagination (skipStateUpdate is false/undefined)
      const includeSelectionCriteria = skipStateUpdate === true;
      const query = buildAssetQuery(
        sessionConfig,
        params,
        ASSET_REPORT_FIELD_NAMES,
        true,
        includeSelectionCriteria
      );
      const sort = buildAssetSort(sessionConfig, ASSET_REPORT_FIELD_NAMES);
      return {
        requestBody: {
          page,
          size,
          query,
          sort,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>, state) => {
      if (
        response &&
        hasApiResult<PagedResult<Partial<ApiReadinessItem>>>(response)
      ) {
        const errors: BDError[] = [];
        const { items, total_items } = response.result;
        const { isNotEquippedFeatureFlag, isNewConnectivityStatusFeatureFlag } =
          getConnectivityFeatureFlags(state);
        const result = {
          ...response,
          result: {
            total_items,
            items: items?.length
              ? items.reduce((mappedAssets, apiAsset) => {
                  try {
                    mappedAssets.push(
                      mapLegacyApiAssetReadinessToReportItem(
                        apiAsset,
                        isNotEquippedFeatureFlag,
                        isNewConnectivityStatusFeatureFlag
                      )
                    );
                  } catch (e) {
                    // handle mapping errors without bombing entire list response
                    errors.push({
                      name: 'Get Vehicle Notification Assets error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'Failed to map asset readiness API response',
                      data: apiAsset,
                    });
                  }
                  return mappedAssets;
                }, [] as AssetReportItem[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      // bomb entire list response if response does not match expected format
      throw new BDError('Unexpected asset report response', { data: response });
    },
  })
);

const getPeripheralAssetListQuerySessionConfig = ({
  state,
  sessionId,
}: {
  sessionId: string;
  state: RootState;
}): Partial<AssetListSession> | undefined => {
  const sessionConfig =
    state.assets.sessionConfigs?.[PeripheralVehiclesConfig.LIST_VIEW]?.[
      sessionId
    ];
  return sessionConfig;
};

const getPeripheralVehicles = makeThunk(
  AssetsActionType.GET_PERIPHERAL_VEHICLES,
  makePostPayloadCreator<
    ApiResponse<PagedResultWithErrors<AssetReportItem>>,
    AssetPaginatedListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.vehicleListOverviewAPI}/views/readinessReport`,
    axiosOptions: ({ organizationsId, fleetsId }, state) =>
      addAcceptLanguageHeader(
        addOrganizationIdHeader(
          addHeader(BASE_CONFIG, BD_FLEET_HEADER_NAME, fleetsId),
          organizationsId
        ),
        state.profile.currentLocale
      ),
    argAdapter: (
      {
        page,
        rowsPerPage: size,
        sessionId,
        continuationToken,
        skipStateUpdate,
        ...params
      },
      state
    ) => {
      const sessionConfig = getPeripheralAssetListQuerySessionConfig({
        sessionId,
        state,
      });

      // Include selection criteria only for dialog data fetching (skipStateUpdate is true)
      // Skip selection criteria for normal pagination (skipStateUpdate is false/undefined)
      const includeSelectionCriteria = skipStateUpdate === true;
      const query = buildAssetQuery(
        sessionConfig,
        params,
        ASSET_REPORT_FIELD_NAMES,
        false,
        includeSelectionCriteria
      );
      const sort = buildAssetSort(sessionConfig, ASSET_REPORT_FIELD_NAMES);
      return {
        requestBody: {
          page,
          size,
          query,
          sort,
          token: continuationToken ?? null,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (
        response &&
        hasApiResult<PagedResult<Partial<ApiReadinessItem>>>(response)
      ) {
        const errors: BDError[] = [];
        const { items, total_items, continuation_token } = response.result;
        const result = {
          ...response,
          result: {
            total_items,
            continuation_token,
            items: items?.length
              ? items.reduce((mappedAssets, apiAsset) => {
                  try {
                    mappedAssets.push(
                      mapLegacyApiAssetReadinessToReportItem(apiAsset)
                    );
                  } catch (e) {
                    // handle mapping errors without bombing entire list response
                    errors.push({
                      name: 'Get Peripheral Vehicle Assets error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'Failed to map asset readiness API response',
                      data: apiAsset,
                    });
                  }
                  return mappedAssets;
                }, [] as AssetReportItem[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      // bomb entire list response if response does not match expected format
      throw new BDError('Unexpected asset report response', { data: response });
    },
  })
);

const getAssetListQuerySessionConfig = ({
  state,
  sessionId,
}: {
  sessionId: string;
  state: RootState;
}): Partial<AssetListSession> | undefined => {
  const configType = SessionConfigType.LIST_VIEW;
  const sessionConfig = state.assets.sessionConfigs?.[configType]?.[sessionId];
  // Type assertion needed because sessionConfigs can contain different session types
  // but we only use this for AssetListSession queries
  return sessionConfig as Partial<AssetListSession> | undefined;
};

const getReadinessReport = makeThunk(
  AssetsActionType.GET_READINESS_REPORT,
  makePostPayloadCreator<
    ApiResponse<PagedResultWithErrors<AssetReportItem>>,
    AssetPaginatedListParams
  >({
    url: () =>
      `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.vehicleListOverviewDiagnosticsAPI}/views/vehicleOverviewList`,
    axiosOptions: ({ organizationsId, fleetsId }, state) =>
      addAcceptLanguageHeader(
        addCSFleetIdHeader(
          addCSOrganizationIdHeader(VEHICLE_LIST_CONFIG, organizationsId),
          fleetsId
        ),
        state.profile.currentLocale
      ),
    argAdapter: (
      {
        rowsPerPage: size,
        sessionId,
        continuationToken,
        skipStateUpdate,
        ...params
      },
      state
    ) => {
      const sessionConfig = getAssetListQuerySessionConfig({
        sessionId,
        state,
      });

      // Include selection criteria only for dialog data fetching (skipStateUpdate is true)
      // Skip selection criteria for normal pagination (skipStateUpdate is false/undefined)
      const includeSelectionCriteria = skipStateUpdate === true;
      const query = buildAssetQuery(
        sessionConfig,
        params,
        ASSET_REPORT_FIELD_NAMES,
        false,
        includeSelectionCriteria
      );
      const sort = buildAssetSort(sessionConfig, ASSET_REPORT_FIELD_NAMES);
      return {
        requestBody: {
          size,
          query,
          sort,
          token: continuationToken ?? null,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>, state) => {
      if (
        response &&
        hasApiResult<PagedResult<Partial<ApiReadinessItem>>>(response)
      ) {
        const errors: BDError[] = [];
        const { items, total_items, continuation_token } = response.result;
        const { isNotEquippedFeatureFlag, isNewConnectivityStatusFeatureFlag } =
          getConnectivityFeatureFlags(state);
        const result = {
          ...response,
          result: {
            total_items,
            continuation_token,
            items: items?.length
              ? items.reduce((mappedAssets, apiAsset) => {
                  try {
                    mappedAssets.push(
                      mapApiAssetReadinessToReportItem(
                        apiAsset,
                        isNotEquippedFeatureFlag,
                        isNewConnectivityStatusFeatureFlag
                      )
                    );
                  } catch (e) {
                    // handle mapping errors without bombing entire list response
                    errors.push({
                      name: 'Get Readiness Report error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'Failed to map asset readiness API response',
                      data: apiAsset,
                    });
                  }
                  return mappedAssets;
                }, [] as AssetReportItem[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      // bomb entire list response if response does not match expected format
      throw new BDError('Unexpected asset report response', { data: response });
    },
  })
);

/**
 * Params for getVehiclesWithDriversAndPeripherals action.
 * Extends AssetPaginatedListParams with vehicle assignment specific options.
 */
type VehicleAssignmentParams = AssetPaginatedListParams & {
  /** When true, includes selection criteria (includedVehicleIds/excludedVehicleIds) in query */
  includeSelectionCriteria?: boolean;
};

/**
 * Redux action to fetch vehicles with their assigned drivers and peripherals.
 * Used in the "Change Fleet" modal to display selected vehicles with their assignments.
 *
 * This action follows the same pattern as getReadinessReport:
 * - Uses buildAssetQuery for query construction (search, filters, selection criteria)
 * - Handles pagination via continuation token
 * - Maps API response to VehicleAssignmentItem format
 */
const getVehiclesWithDriversAndPeripherals = makeThunk(
  AssetsActionType.GET_VEHICLES_WITH_DRIVERS_AND_PERIPHERALS,
  makePostPayloadCreator<
    ApiResponse<PagedResultWithErrors<VehicleAssignmentItem>>,
    VehicleAssignmentParams
  >({
    url: () =>
      `${globalThis.appConfig.apiBaseUrl}/assets/2025-12-30/views/vehicles-with-drivers-and-peripherals`,
    axiosOptions: ({ organizationsId, fleetsId, continuationToken }, state) =>
      addAcceptLanguageHeader(
        addHeader(
          addCSFleetIdHeader(
            addCSOrganizationIdHeader(VEHICLE_LIST_CONFIG, organizationsId),
            fleetsId
          ),
          CS_CONTINUATION_TOKEN_HEADER_NAME,
          continuationToken ?? undefined
        ),
        state.profile.currentLocale
      ),
    argAdapter: (
      {
        rowsPerPage: size,
        sessionId,
        includeSelectionCriteria = true,
        ...params
      },
      state
    ) => {
      const sessionConfig = getAssetListQuerySessionConfig({
        sessionId,
        state,
      });

      // Build query using same logic as readinessReport
      const query = buildAssetQuery(
        sessionConfig,
        params,
        ASSET_REPORT_FIELD_NAMES,
        false, // isContextQueryRequired - we're using cs-organization header
        includeSelectionCriteria
      );

      return {
        requestBody: {
          size,
          query,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>, state) => {
      if (
        response &&
        hasApiResult<{
          items: VehicleWithDriverAndPeripheralsResponse[];
          total_items?: number;
          continuation_token?: string;
        }>(response)
      ) {
        const organizationsId =
          state.profile.actingProfile?.organization?.id ?? '';
        const errors: BDError[] = [];
        const { items, total_items, continuation_token } = response.result;
        const result = {
          ...response,
          result: {
            total_items: total_items ?? 0,
            continuation_token,
            items: items?.length
              ? items.reduce((mappedVehicles, apiVehicle) => {
                  try {
                    mappedVehicles.push(
                      ...mapToVehicleAssignmentItems(
                        [apiVehicle],
                        organizationsId
                      )
                    );
                  } catch (e) {
                    errors.push({
                      name: 'Get Vehicles With Drivers And Peripherals error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'Failed to map vehicle assignment API response',
                      data: apiVehicle,
                    });
                  }
                  return mappedVehicles;
                }, [] as VehicleAssignmentItem[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      throw new BDError('Unexpected vehicle assignment response', {
        data: response,
      });
    },
  })
);

const getVehicleDiagnosticsList = makeThunk(
  AssetsActionType.GET_VEHICLE_DIAGNOSTICS_LIST,
  makePostPayloadCreator<
    ApiResponse<PagedResultWithErrors<AssetReportItem>>,
    AssetPaginatedListParams
  >({
    url: () => {
      const url = `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.vehicleListOverviewDiagnosticsAPI}/views/vehicleDiagnosticsList`;
      return url;
    },
    axiosOptions: ({ organizationsId, fleetsId }, state) =>
      addAcceptLanguageHeader(
        addCSFleetIdHeader(
          addCSOrganizationIdHeader(VEHICLE_LIST_CONFIG, organizationsId),
          fleetsId
        ),
        state.profile.currentLocale
      ),
    argAdapter: (
      {
        rowsPerPage: size,
        sessionId,
        continuationToken,
        skipStateUpdate,
        ...params
      },
      state
    ) => {
      const sessionConfig = getAssetListQuerySessionConfig({
        sessionId,
        state,
      });
      // Include selection criteria only for dialog data fetching (skipStateUpdate is true)
      // Skip selection criteria for normal pagination (skipStateUpdate is false/undefined)
      const includeSelectionCriteria = skipStateUpdate === true;
      const isTwelveVoltBatteryEnabled = Boolean(
        state.featureFlags.entities[BATTERY_12V_PROGNOSTICS_FLAG]?.value
      );
      const query = buildAssetQuery(
        sessionConfig,
        params,
        ASSET_REPORT_FIELD_NAMES,
        false,
        includeSelectionCriteria,
        isTwelveVoltBatteryEnabled
      );
      const sort = buildAssetSort(sessionConfig, ASSET_REPORT_FIELD_NAMES);
      return {
        requestBody: {
          size,
          query,
          sort,
          token: continuationToken ?? null,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>, state) => {
      if (
        response &&
        hasApiResult<PagedResult<Partial<ApiReadinessItem>>>(response)
      ) {
        const errors: BDError[] = [];
        const { items, total_items, continuation_token } = response.result;
        const isTwelveVoltBatteryEnabled = Boolean(
          state.featureFlags.entities[BATTERY_12V_PROGNOSTICS_FLAG]?.value
        );
        const result = {
          ...response,
          result: {
            total_items,
            continuation_token,
            items: items?.length
              ? items.reduce((mappedAssets, apiAsset) => {
                  try {
                    mappedAssets.push(
                      mapApiAssetDiagnosticsToReportItem(
                        apiAsset,
                        isTwelveVoltBatteryEnabled
                      )
                    );
                  } catch (e) {
                    // handle mapping errors without bombing entire list response
                    errors.push({
                      name: 'Get Vehicle Diagnostics List error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'Failed to map vehicle diagnostics list API response',
                      data: apiAsset,
                    });
                  }
                  return mappedAssets;
                }, [] as AssetReportItem[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      throw new BDError('Unexpected vehicle diagnostics list response', {
        data: response,
      });
    },
  })
);

const getTiresReport = makeThunk(
  AssetsActionType.GET_TIRES_REPORT,
  makePostPayloadCreator<
    ApiResponse<PagedResultWithErrors<AssetReportItem>>,
    AssetPaginatedListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/views/tireReadinessReport`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: (
      { page, rowsPerPage: size, sessionId, skipStateUpdate, ...params },
      state
    ) => {
      const sessionConfig = getAssetListQuerySessionConfig({
        sessionId,
        state,
      });

      // Include selection criteria only for dialog data fetching (skipStateUpdate is true)
      // Skip selection criteria for normal pagination (skipStateUpdate is false/undefined)
      const includeSelectionCriteria = skipStateUpdate === true;
      const query = buildAssetQuery(
        sessionConfig,
        params,
        ASSET_REPORT_FIELD_NAMES,
        true,
        includeSelectionCriteria
      );
      const sort = buildAssetSort(sessionConfig, ASSET_REPORT_FIELD_NAMES);
      return {
        requestBody: {
          page,
          size,
          query,
          sort,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (
        response &&
        hasApiResult<PagedResult<Partial<ApiTiresReadinessItem>>>(response)
      ) {
        const errors: BDError[] = [];
        const { items, total_items } = response.result;
        const result = {
          ...response,
          result: {
            total_items,
            items: items?.length
              ? items.reduce((mappedAssets, apiAsset) => {
                  try {
                    mappedAssets.push(
                      mapApiAssetTireReadinessToReportItem(apiAsset)
                    );
                  } catch (e) {
                    // handle mapping errors without bombing entire list response
                    errors.push({
                      name: 'Get Tires Report error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'Failed to map asset tire readiness API response',
                      data: apiAsset,
                    });
                  }
                  return mappedAssets;
                }, [] as AssetReportItem[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      // bomb entire list response if response does not match expected format
      throw new BDError('Unexpected asset tire report response', {
        data: response,
      });
    },
  })
);

const getBatteryReport = makeThunk(
  AssetsActionType.GET_BATTERY_REPORT,
  makePostPayloadCreator<
    ApiResponse<PagedResultWithErrors<AssetReportItem>>,
    AssetPaginatedListParams
  >({
    url: () => {
      const apiVersion =
        API_VERSION_DEFAULTS.vehicleListBatteryConnectedOnlyAPI;
      return `${globalThis.appConfig.apiBaseUrl}/assets/${apiVersion}/views/batteryReadinessReport`;
    },
    axiosOptions: ({ organizationsId, fleetsId }, state) =>
      addAcceptLanguageHeader(
        addCSFleetIdHeader(
          addCSOrganizationIdHeader(VEHICLE_LIST_CONFIG, organizationsId),
          fleetsId
        ),
        state.profile.currentLocale
      ),

    argAdapter: (
      {
        page,
        rowsPerPage: size,
        sessionId,
        continuationToken,
        skipStateUpdate,
        ...params
      },
      state
    ) => {
      const sessionConfig = getAssetListQuerySessionConfig({
        sessionId,
        state,
      });

      // Include selection criteria only for dialog data fetching (skipStateUpdate is true)
      // Skip selection criteria for normal pagination (skipStateUpdate is false/undefined)
      const includeSelectionCriteria = skipStateUpdate === true;
      const query = buildAssetQuery(
        sessionConfig,
        params,
        ASSET_REPORT_FIELD_NAMES,
        false,
        includeSelectionCriteria
      );
      const sort = buildAssetSort(sessionConfig, ASSET_REPORT_FIELD_NAMES);
      return {
        requestBody: {
          page,
          size,
          query,
          sort,
          token: continuationToken ?? null,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>, state) => {
      if (
        response &&
        hasApiResult<PagedResult<Partial<ApiBatteryReadinessItem>>>(response)
      ) {
        const errors: BDError[] = [];
        const { items, total_items, continuation_token } = response.result;
        const { isNotEquippedFeatureFlag, isNewConnectivityStatusFeatureFlag } =
          getConnectivityFeatureFlags(state);
        const result = {
          ...response,
          result: {
            total_items,
            continuation_token,
            items: items?.length
              ? items.reduce((mappedAssets, apiAsset) => {
                  try {
                    mappedAssets.push(
                      mapApiAssetBatteryReadinessToReportItem(
                        apiAsset,
                        isNotEquippedFeatureFlag,
                        isNewConnectivityStatusFeatureFlag
                      )
                    );
                  } catch (e) {
                    errors.push({
                      name: 'Get Battery Report error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'Failed to map asset battery readiness API response',
                      data: apiAsset,
                    });
                  }
                  return mappedAssets;
                }, [] as AssetReportItem[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      throw new BDError('Unexpected asset battery report response', {
        data: response,
      });
    },
  })
);
const getRecalls = makeThunk(
  AssetsActionType.GET_RECALLS,
  makePostPayloadCreator<
    ApiResponse<PagedResultWithErrors<AssetReportItem>>,
    AssetPaginatedListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.recallsAPI}/fieldactions/vehicleFieldActionReport`,
    axiosOptions: ({ organizationsId, fleetsId }, state) =>
      addAcceptLanguageHeader(
        addOrganizationIdHeader(
          addHeader(VEHICLE_LIST_CONFIG, BD_FLEET_HEADER_NAME, fleetsId),
          organizationsId
        ),
        state.profile.currentLocale
      ),
    argAdapter: (
      { rowsPerPage: size, sessionId, continuationToken },
      state
    ) => {
      const sessionConfig = getAssetListQuerySessionConfig({
        sessionId,
        state,
      });

      const filterCriteria = formatFilterCriteriaForVehicles(
        sessionConfig?.filterType
      );
      const searchCriteria = formatSearchCriteriaForVehicles(
        sessionConfig?.searchCriteria
      );

      return {
        requestBody: {
          size,
          filterCriteria,
          searchCriteria,
          token: continuationToken,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<PagedResult<ApiRecallsItem>>(response)) {
        const errors: BDError[] = [];
        const { items, total_items, continuation_token } = response.result;
        const result = {
          ...response,
          result: {
            total_items,
            continuation_token,
            items: items?.length
              ? items.reduce((mappedAssets, apiRecall) => {
                  try {
                    mappedAssets.push(mapApiRecallsToReportItem(apiRecall));
                  } catch (e) {
                    // handle mapping errors without bombing entire list response
                    errors.push({
                      name: 'Get Recall list error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'Failed to map recall list API response',
                      data: apiRecall,
                    });
                  }
                  return mappedAssets;
                }, [] as AssetReportItem[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      // bomb entire list response if response does not match expected format
      throw new BDError('Unexpected Recall list response', {
        data: response,
      });
    },
  })
);
const getAssetsCompliance = makeThunk(
  AssetsActionType.GET_ASSETS_COMPLIANCE,
  makePostPayloadCreator<
    ApiResponse<PagedResultWithErrors<AssetComplianceDetail>>,
    AssetPaginatedListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.assetComplianceAPI}/views/complianceReport`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(DEFAULT_API_CONFIG, state.profile.currentLocale),
    argAdapter: ({ page, rowsPerPage: size, sessionId, ...params }, state) => {
      const sessionConfig =
        state.assets.sessionConfigs?.[AssetReportConfigType.COMPLIANCE_VIEW]?.[
          sessionId
        ];
      const isChargeStatusFlagEnabled =
        !!state.featureFlags.entities[CHARGING_SCHEDULE_FLAG_NAME]?.value;
      const query = buildAssetComplianceQuery(
        sessionConfig,
        params,
        isChargeStatusFlagEnabled
      );
      const sort = buildAssetSort(sessionConfig, ASSET_REPORT_FIELD_NAMES);
      return {
        requestBody: {
          page,
          size,
          query,
          sort,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (
        response &&
        hasApiResult<PagedResult<Partial<AssetComplianceDetail>>>(response)
      ) {
        const errors: BDError[] = [];
        const { items, total_items } = response.result;
        const result = {
          ...response,
          result: {
            total_items,
            items: items?.length
              ? items.reduce((mappedAssets, apiAsset) => {
                  try {
                    mappedAssets.push(
                      mapApiAssetComplianceToAssetComplianceDetail(apiAsset)
                    );
                  } catch (e) {
                    // handle mapping errors without bombing entire list response
                    errors.push({
                      name: 'Get Assets Compliance error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'Failed to map Asset compliance API response',
                      data: apiAsset,
                    });
                  }
                  return mappedAssets;
                }, [] as AssetComplianceDetail[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      // bomb entire list response if response does not match expected format
      throw new BDError('Unexpected asset compliance response', {
        data: response,
      });
    },
  })
);

const searchAssets = makeThunk(
  AssetsActionType.GET_ASSETS_SEARCH,
  makePostPayloadCreator<
    ApiResponse<PagedResult<Asset>>,
    AssetPaginatedListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/searches`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: (
      { page, rowsPerPage: size, sessionId, skipStateUpdate, ...contextFilter },
      state
    ) => {
      const sessionConfig =
        state.assets.sessionConfigs?.[SearchConfigType.SEARCH_VIEW]?.[
          sessionId
        ];
      // Include selection criteria only for dialog data fetching (skipStateUpdate is true)
      // Skip selection criteria for normal pagination (skipStateUpdate is false/undefined)
      const includeSelectionCriteria = skipStateUpdate === true;
      const query = buildAssetQuery(
        sessionConfig,
        contextFilter,
        ASSET_REPORT_FIELD_NAMES,
        true,
        includeSelectionCriteria
      );
      const sort = buildAssetSort(sessionConfig, ASSET_REPORT_FIELD_NAMES);
      return {
        requestBody: {
          page,
          size,
          query,
          sort,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>, state) => {
      if (response && hasApiResult<PagedResult<Partial<ApiAsset>>>(response)) {
        const errors: BDError[] = [];
        const { items, total_items } = response.result;
        const { isNotEquippedFeatureFlag, isNewConnectivityStatusFeatureFlag } =
          getConnectivityFeatureFlags(state);
        const result = {
          ...response,
          result: {
            total_items,
            items: items?.length
              ? items.reduce((mappedAssets, apiAsset) => {
                  try {
                    mappedAssets.push(
                      mapAssetApiResponseToAsset(
                        apiAsset,
                        isNotEquippedFeatureFlag,
                        isNewConnectivityStatusFeatureFlag,
                        {
                          isTwelveVoltBatteryEnabled: Boolean(
                            state.featureFlags.entities[
                              BATTERY_12V_PROGNOSTICS_FLAG
                            ]?.value
                          ),
                        }
                      )
                    );
                  } catch (e) {
                    // handle mapping errors without bombing entire list response
                    errors.push({
                      name: 'Search Assets error',
                      type: BDAppErrorType.VALIDATION,
                      message:
                        e instanceof Error
                          ? e.message
                          : 'failed to map Asset API response',
                      data: apiAsset,
                    });
                  }
                  return mappedAssets;
                }, [] as Asset[])
              : [],
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      // bomb entire list response if response does not match expected format
      throw new BDError('Unexpected asset list response', { data: response });
    },
  })
);

const getAssetDetails = makeThunk(
  AssetsActionType.GET_ASSET_DETAILS,
  makeGetPayloadCreator<ApiResponse<Asset>, Required<AssetRouteParams>>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/views/enhancements/:assetsId`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(DEFAULT_API_CONFIG, state.profile.currentLocale),
    argAdapter: ({ assetsId }) => ({ requestParams: { assetsId } }),
    responseAdapter: (response: unknown, state): ApiResponse<Asset> => {
      if (response && hasApiResult<ApiAsset>(response)) {
        const errors: BDError[] = [];
        const apiAsset = response.result;
        const { isNotEquippedFeatureFlag, isNewConnectivityStatusFeatureFlag } =
          getConnectivityFeatureFlags(state);
        try {
          // Map API response to UI Asset
          const mappedAsset = mapAssetApiResponseToAsset(
            apiAsset,
            isNotEquippedFeatureFlag,
            isNewConnectivityStatusFeatureFlag,
            {
              isTwelveVoltBatteryEnabled: Boolean(
                state.featureFlags.entities[BATTERY_12V_PROGNOSTICS_FLAG]?.value
              ),
            }
          );

          const diagnosticsError = checkDiagnosticsError(mappedAsset);
          if (diagnosticsError) {
            errors.push({
              name: 'Asset Diagnostic Error',
              type: BDAppErrorType.VALIDATION,
              message: diagnosticsError,
            });
          }
          return {
            ...response,
            result: mappedAsset,
            errors,
          };
        } catch (e) {
          // handle mapping errors without bombing entire list response
          errors.push({
            name: 'Asset Details API Error',
            type: BDAppErrorType.API,
            message:
              e instanceof Error
                ? e.message
                : 'Failed to map asset API response',
            data: apiAsset,
          });

          return {
            ...response,
            errors: [
              BDError.asJson(
                e instanceof Error
                  ? e
                  : new Error('Failed to parse asset detail response')
              ),
            ],
          } as ApiResponse<Asset>;
        }
      }
      throw new BDError('Unexpected asset detail response', { data: response });
    },
  })
);

const getAssetRecalls = makeThunk(
  AssetsActionType.GET_ASSET_RECALLS,
  makeGetPayloadCreator<
    ApiResponse<AssetRecall[] | null>,
    Required<AssetRouteParams>
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.assetRecallsAPI}/fieldactions/lookup/:assetsId`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(DEFAULT_API_CONFIG, state.profile.currentLocale),
    argAdapter: ({ assetsId }) => ({ requestParams: { assetsId } }),
    responseAdapter: (response: unknown): ApiResponse<AssetRecall[] | null> => {
      if (response && hasApiResult<ApiAssetRecall>(response)) {
        const errors: BDError[] = [];
        const apiAssetRecalls = response.result.fieldActions;
        try {
          //null case is to handle when Field action not found for vin
          const recalls = apiAssetRecalls
            ? mapVehicleDetailsRecalls(apiAssetRecalls)
            : null;
          return {
            ...response,
            result: recalls,
            errors,
          };
        } catch (e) {
          // handle mapping errors without bombing entire list response
          errors.push({
            name: 'Asset Details Recalls API Error',
            type: BDAppErrorType.API,
            message:
              e instanceof Error
                ? e.message
                : 'Failed to map asset API response',
            data: apiAssetRecalls,
          });

          return {
            ...response,
            result: response.result?.fieldActions,
            errors: [
              BDError.asJson(
                e instanceof Error
                  ? e
                  : new Error('Failed to parse asset detail response')
              ),
            ],
          } as ApiResponse<AssetRecall[]>;
        }
      }
      throw new BDError('Unexpected asset detail response', {
        data: response,
      });
    },
  })
);

const getAssetHistory = makeThunk(
  AssetsActionType.GET_ASSET_HISTORY,
  makeGetPayloadCreator<ApiResponse<AssetEvent[]>, AssetHistoryParams>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.assetHistoryAPI}/:assetsId/history`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: ({
      assetsId,
      startDate,
      endDate,
      actions = [
        AssetEventAction.POWER_ON,
        AssetEventAction.POWER_OFF,
        AssetEventAction.LOCK,
        AssetEventAction.DOOR,
      ],
    }) => ({
      requestParams: {
        assetsId,
        startDate,
        endDate,
        actions: actions.join(','),
      },
    }),
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      const typed = response as ApiResponse<{
        items?: Parameters<typeof mapEventApiResponseToEvent>[0];
      }>;
      return {
        ...typed,
        result: typed.result?.items?.length
          ? mapEventApiResponseToEvent(typed.result.items)
          : [],
      };
    },
  })
);

const getAssetLocation = makeThunk(
  AssetsActionType.GET_ASSET_LOCATION,
  makeGetPayloadCreator<ApiResponse<AssetRawLocation[]>, AssetHistoryParams>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.assetLocationsAPI}/:assetsId/location/geojson`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: ({ assetsId, startDate, endDate }) => ({
      requestParams: {
        assetsId,
        startDate,
        endDate,
      },
    }),
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      const typed = response as ApiResponse<{
        geojson?: Parameters<typeof mapGeoJSONApiResponseToLocation>[0];
      }>;
      return {
        ...typed,
        result: typed.result?.geojson?.geometry?.coordinates?.length
          ? mapGeoJSONApiResponseToLocation(typed.result.geojson)
          : {},
      } as ApiResponse<AssetRawLocation[]>;
    },
  })
);

const addAsset = makeThunk(
  AssetsActionType.ADD_ASSET,
  makePostPayloadCreator<ApiResponse<Asset>, AddAssetFormFields>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}`,
    argAdapter: (assetForm) => ({ requestBody: assetForm }),
  })
);

const rejectVehicle = makeThunk(
  AssetsActionType.REJECT_VEHICLE,
  makePostPayloadCreator<
    ApiResponse<RejectApproveVehiclesResult>,
    RejectVehicle
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.rejectConfirmVehicleAPI}/onboarding/vehicles/ownership/reject`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: (payload) => ({ requestBody: payload }),
  })
);

const retryVehicle = makeThunk(
  AssetsActionType.RETRY_VEHICLE,
  makePostPayloadCreator<
    ApiResponse<RetryVehicleResponseBody>,
    RetryVehicleRequestBody
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.retryOnboardingVehicleAPI}/onboarding/vehicles/ownership/retry`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: (payload) => ({ requestBody: payload }),
  })
);

const approveVehicle = makeThunk(
  AssetsActionType.APPROVE_VEHICLE,
  makePostPayloadCreator<
    ApiResponse<RejectApproveVehiclesResult>,
    ApproveVehicle
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.rejectConfirmVehicleAPI}/onboarding/vehicles/ownership/confirm`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: (payload) => ({ requestBody: payload }),
  })
);

const reProvisionAsset = makeThunk(
  AssetsActionType.UPDATE_ASSET,
  makePutPayloadCreator<ApiResponse<void>, { assetId: string }>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/admin/telemetry/:assetId`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: ({ assetId }) => ({
      requestParams: { assetId },
    }),
  })
);
const saveAssetProtectedFields = makeThunk(
  AssetsActionType.UPDATE_ASSET,
  makePostPayloadCreator<
    ApiResponse<void>,
    AssetProtectedFormFields & { assetId: string }
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/admin/:assetId`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: ({ assetId, ...fields }) => ({
      requestParams: { assetId },
      requestBody: fields,
    }),
  })
);

const updateAsset = makeThunk(
  AssetsActionType.UPDATE_ASSET,
  makePutPayloadCreator<ApiResponse<Asset>, UpdateAssetFormPayload>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/:assetId`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: (payload) => ({
      requestParams: { assetId: payload.params.assetsId },
      requestBody: mapAssetFormToApiRequest(payload),
    }),
  })
);

const lockAsset = makeThunk(
  AssetsActionType.LOCK_ASSET,
  makePutPayloadCreator<
    ApiResponse,
    FormPayload<{ value: boolean }, keyof AssetRouteParams>
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/:assetId/status/lock`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: ({ formFields, params }) => ({
      requestParams: { assetId: params.assetsId },
      requestBody: formFields,
    }),
  })
);

const updateServiceAvailability = makeThunk(
  AssetsActionType.UPDATE_AVAILABILITY,
  makePostPayloadCreator<
    ApiResponse<Asset>,
    {
      assetId: string;
      currentState: AssetServiceStatus;
      desiredState: AssetServiceStatus;
    }
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/:assetId/states/availability`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: ({ assetId, currentState, desiredState }) => ({
      requestParams: { assetId },
      requestBody: {
        current: currentState,
        desired: desiredState,
      },
    }),
  })
);

const deleteAsset = makeThunk(
  AssetsActionType.DELETE_ASSET,
  makeDeletePayloadCreator<ApiResponse, string>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/:assetId`,
    argAdapter: (assetId) => ({
      requestParams: { assetId },
    }),
  })
);

const getNextEPalletSerial = makeThunk(
  AssetsActionType.GET_NEXT_EP_SERIAL,
  makeGetPayloadCreator<ApiResponse<string>, void>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/nextEPalletSerial`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
  })
);

const getTrackerSerials = makeThunk(
  AssetsActionType.GET_TRACKER_SERIALS,
  makeGetPayloadCreator<ApiResponse<string[]>, void>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/trackerSerials`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
  })
);

const getEPalletVersions = makeThunk(
  AssetsActionType.GET_EP_VERSIONS,
  makeGetPayloadCreator<ApiResponse<AssetModel[]>, void>({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/ePalletVersions`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
  })
);

type RemoteCommandParams = {
  type: string;
  command: string;
  assetId: string;
  organizationsId: string;
};

const executeRemoteCommand = makeThunk(
  AssetsActionType.REMOTE_COMMAND_ASSET,
  makeGetPayloadCreator<
    ApiResponse<AssetRemoteCommandExecuteResult>,
    RemoteCommandParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.remoteCommandsAPI}/remotecommands/:type/:command/:assetId`,
    axiosOptions: ({ organizationsId }, state) =>
      addCSOrganizationIdHeader(
        addAcceptLanguageHeader(
          REMOTE_COMMAND_API_CONFIG,
          state.profile.currentLocale
        ),
        organizationsId
      ),
    argAdapter: (params) => ({
      requestParams: {
        assetId: params.assetId,
        command: params.command,
        type: params.type,
      },
    }),
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<AssetRemoteCommandExecuteResult>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected remote command response', {
        data: response,
      });
    },
  })
);

type AssetStatusRequestParams = AssetListParams & {
  assetId: string;
  assetStatuses: Set<ApiAssetTelemetryKey>;
};

const getAssetStatuses = makeThunk(
  AssetsActionType.GET_ASSET_STATUS,
  makeGetPayloadCreator<
    ApiResponse<AssetStatusResult>,
    AssetStatusRequestParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.assetStatusesAPI}/views/status/:assetId?statuses=:assetStatuses`,
    axiosOptions: ({ organizationsId, fleetsId }, state) =>
      addAcceptLanguageHeader(
        addCSFleetIdHeader(
          addCSOrganizationIdHeader(BASE_CONFIG, organizationsId),
          fleetsId
        ),
        state.profile.currentLocale
      ),
    argAdapter: (params) => ({
      requestParams: {
        assetId: params.assetId,
        assetStatuses: [...params.assetStatuses].join(','),
      },
    }),
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<AssetStatusResult>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected get asset status response', {
        data: response,
      });
    },
  })
);

const getAssetIgnition = makeThunk(
  AssetsActionType.GET_ASSET_IGNITION,
  makeGetPayloadCreator<
    ApiResponse<{ ignitionStatus: AssetIgnitionStatus }>,
    { assetId: string }
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.assetIgnitionAPI}/views/vehicleIgnitionStatus/:assetId`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(BASE_CONFIG, state.profile.currentLocale),
    argAdapter: (params) => ({
      requestParams: {
        assetId: params.assetId,
      },
    }),
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (
        response &&
        hasApiResult<{ ignitionStatus: AssetIgnitionStatus }>(response)
      ) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected get asset status response', {
        data: response,
      });
    },
  })
);
const getAdvisorVehicleList = makeThunk(
  AssetsActionType.GET_ADVISOR_VEHICLE_LIST,
  makeGetPayloadCreator<
    ApiResponse<PagedResultWithErrors<AdvisorVehicleListItem>>,
    AssetPaginatedListParams & SearchFilterInfo
  >({
    url: (params) =>
      `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.advisorVehicleListApi}/views/vehicleList?size=${params.rowsPerPage}`,
    axiosOptions: ({ continuationToken, organizationsId, fleetsId }, state) => {
      const commonHeaders = addAcceptLanguageHeader(
        addCSFleetIdHeader(
          addCSOrganizationIdHeader(BASE_CONFIG, organizationsId),
          fleetsId
        ),
        state.profile.currentLocale
      );
      return continuationToken
        ? addHeader(
            commonHeaders,
            CS_CONTINUATION_TOKEN_HEADER_NAME,
            continuationToken
          )
        : commonHeaders;
    },
    argAdapter: ({ searchCriteria, filterCriteria }) => {
      const params = buildAdvisorVehicleQueryParam({
        search: searchCriteria,
        filter: filterCriteria,
      });
      return {
        requestParams: params,
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>, state) => {
      if (
        response &&
        hasApiResult<PagedResult<AdvisorVehicleListItem>>(response)
      ) {
        const { items, total_items, continuation_token } = response.result;
        const errors: BDError[] = [];
        const { isNotEquippedFeatureFlag, isNewConnectivityStatusFeatureFlag } =
          getConnectivityFeatureFlags(state);
        const mappedItems = items?.length
          ? items.map((item) =>
              mapApiAdvisorVehicleListItem(
                item,
                isNotEquippedFeatureFlag,
                isNewConnectivityStatusFeatureFlag
              )
            )
          : [];
        const result = {
          ...response,
          result: {
            total_items,
            items: mappedItems,
            continuation_token,
          },
        };
        return errors.length
          ? { ...result, errors: errors.map((e) => BDError.asJson(e)) }
          : result;
      }
      throw new BDError('Unable to parse org list response', {
        type: BDAppErrorType.VALIDATION,
        data: { response },
      });
    },
  })
);

export const ASSETS_ACTIONS = {
  getAssets,
  getPendingVehicles,
  getReadinessReport,
  getVehiclesWithDriversAndPeripherals,
  getTiresReport,
  getBatteryReport,
  getVehicleDiagnosticsList,
  getRecalls,
  getAssetsCompliance,
  getVehicleNotificationAssets,
  getPeripheralVehicles,
  searchAssets,
  getAssetDetails,
  getAssetRecalls,
  getAssetHistory,
  getAssetLocation,
  addAsset,
  rejectVehicle,
  retryVehicle,
  approveVehicle,
  updateAsset,
  lockAsset,
  updateServiceAvailability,
  deleteAsset,
  getNextEPalletSerial,
  getTrackerSerials,
  getEPalletVersions,
  reProvisionAsset,
  saveAssetProtectedFields,
  executeRemoteCommand,
  getAssetStatuses,
  getVehicleListFluidsExport,
  getAssetIgnition,
  getAdvisorVehicleList,
};
